import {
  Box,
  Button,
  Container,
  Typography,
  Stack,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Paper,
  Dialog,
  DialogContent,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getFirestore,
  collection,
  onSnapshot,
  addDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionIcon from "@mui/icons-material/Description";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import { fetchSessionSummary } from "../../services/sessionService";

export function ConfirmarAta() {
  const { id: roomId } = useParams();
  const [transcription, setTranscription] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<
    "idle" | "generating" | "saving"
  >("idle");
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [ataGerada, setAtaGerada] = useState<any>(null);
  const [loadingAta, setLoadingAta] = useState(false);
  const [showAtaCompleta, setShowAtaCompleta] = useState(false);
  const [sessaoData, setSessao] = useState<any>(null);
  const [presencasData, setPresencas] = useState<any[]>([]);
  const [pautasData, setPautas] = useState<any[]>([]);

  type AtaGerada = {
    id: string;
    texto: string;
    criadoEm: any;
    modeloUsado: string;
    transcricaoId: string;
    sessaoId?: string;
    geradaComIA?: boolean;
    status?: string;
    versao?: string;
  };

  useEffect(() => {
    if (!roomId) return;

    const db = getFirestore();
    const transcriptionsRef = collection(db, "transcriptions");

    const unsubscribe = onSnapshot(transcriptionsRef, (querySnapshot) => {
      // Buscar documentos onde o campo 'sessao' corresponde ao roomId
      const matchingDocs = querySnapshot.docs.filter((doc) => {
        const data = doc.data();
        return data.sessao === roomId;
      });

      if (matchingDocs.length > 0) {
        // Pegar o documento mais recente (último criado)
        const latestDoc = matchingDocs[matchingDocs.length - 1];
        const data = latestDoc.data();
        setTranscription({ id: latestDoc.id, ...data });
      } else {
        setTranscription(null);
      }
    });
    return () => unsubscribe();
  }, [roomId]);

  // Buscar atas geradas para esta sessão
  useEffect(() => {
    if (!roomId) return;

    setLoadingAta(true);
    const db = getFirestore();

    // Tentar primeiro com belacruz@eleg
    const atasRef = collection(
      db,
      "users",
      `belacruz@eleg`,
      "sessoes",
      `${roomId}`,
      "atasIA"
    );

    const unsubscribe = onSnapshot(atasRef, (querySnapshot) => {
      const atas = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (atas.length > 0) {
        // Pegar a ata mais recente
        const ataMaisRecente = atas.sort(
          (a, b) =>
            new Date(
              (b as AtaGerada).criadoEm?.toDate?.() ||
                (b as AtaGerada).criadoEm ||
                0
            ).getTime() -
            new Date(
              (a as AtaGerada).criadoEm?.toDate?.() ||
                (a as AtaGerada).criadoEm ||
                0
            ).getTime()
        )[0];
        setAtaGerada(ataMaisRecente);
        setLoadingAta(false);
      } else {
        // Se não encontrou com belacruz@eleg, tentar com belacruz@eleg
        console.log(
          "Nenhuma ata encontrada com belacruz@eleg, tentando belacruz@eleg..."
        );
        const atasRefFallback = collection(
          db,
          "users",
          `belacruz@eleg`,
          "sessoes",
          `${roomId}`,
          "atasIA"
        );

        const unsubscribeFallback = onSnapshot(
          atasRefFallback,
          (querySnapshotFallback) => {
            const atasFallback = querySnapshotFallback.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));

            if (atasFallback.length > 0) {
              const ataMaisRecenteFallback = atasFallback.sort(
                (a, b) =>
                  new Date(
                    (b as AtaGerada).criadoEm?.toDate?.() ||
                      (b as AtaGerada).criadoEm ||
                      0
                  ).getTime() -
                  new Date(
                    (a as AtaGerada).criadoEm?.toDate?.() ||
                      (a as AtaGerada).criadoEm ||
                      0
                  ).getTime()
              )[0];
              setAtaGerada(ataMaisRecenteFallback);
            } else {
              setAtaGerada(null);
            }
            setLoadingAta(false);
          }
        );

        return () => unsubscribeFallback();
      }
    });

    return () => unsubscribe();
  }, [roomId]);

  useEffect(() => {
    if (!roomId) return;
    fetchSessionSummary(roomId)
      .then(({ sessao, presencas, pautas }) => {
        setSessao(sessao);
        setPresencas(presencas);
        setPautas(pautas);
      })
      .catch((e) => {
        console.warn("Falha ao buscar dados da sessão:", e);
      });
  }, [roomId]);

  const handleGenerateAta = async () => {
    console.log("Botão clicado - iniciando geração de ata");

    if (!transcription?.text) {
      console.log("Erro: Não há transcrição disponível");
      setError("Não há transcrição disponível para gerar a ata");
      return;
    }

    console.log(
      "Transcrição encontrada:",
      transcription.text.substring(0, 100) + "..."
    );

    setLoading(true);
    setError(null);
    setCurrentStep("generating");

    try {
      console.log("Iniciando chamada para API do ChatGPT...");

      // Truncar a transcrição para caber no limite de tokens
      const maxTranscriptionLength = 3000; // Limitar a transcrição
      const truncatedTranscription =
        transcription.text.length > maxTranscriptionLength
          ? transcription.text.substring(0, maxTranscriptionLength) +
            "... [continua]"
          : transcription.text;

      // Modelo de ata mais compacto
      const modeloAtaCompacto = `ATA DA [NÚMERO DA SESSÃO, POR EXTENSO] SESSÃO [TIPO] DO [PERÍODO E ANO] DA [NÚMERO DA LEGISLATURA] LEGISLATURA DA CÂMARA MUNICIPAL DE [NOME DO MUNICÍPIO].

Aos [DIA, POR EXTENSO] de [MÊS] de [ANO, POR EXTENSO] às [HORA] no paço da Câmara Municipal, reuniram-se os vereadores deste Município, SOB A PRESIDÊNCIA DE [PRESIDENTE], secretariando os trabalhos o vereador [SECRETARIO].

Declarada aberta a sessão, o Senhor Presidente solicitou ao Secretário que fizesse a leitura da Ata da [NÚMERO DA SESSÃO ANTERIOR] Sessão Ordinária, realizada em [DATA DA SESSÃO ANTERIOR], a qual, após lida, foi posta em discussão, votação e [RESULTADO DA VOTAÇÃO DA ATA ANTERIOR].

Em seguida, o Presidente passou ao EXPEDIENTE, que constou da leitura de [MATERIAIS DIVERSOS LIDOS NO EXPEDIENTE, CASO HAJA].

Passando ao GRANDE EXPEDIENTE, o Senhor Presidente informou [REGISTRO SOBRE A INSCRIÇÃO DOS VEREADORES NA TRIBUNA. Ex: não haver vereadores inscritos para o uso da tribuna], prosseguindo, abriu espaço para as inscrições na TRIBUNA DO POVO, onde fizeram uso da palavra os cidadãos: [NOMES DOS CIDADÃOS, COM O REGISTRO SUCINTO DE SEUS TEMAS. Ex: Sra. Maria Rosimeire Freitas (solicitação e comentários sobre a Lei 940/2022); Francisco Dário Rios e Pedro Gustavo Lopes (incentivo financeiro para feira científica); Sr. Robério (Projeto "Adote um Sorriso")].

Em seguida, passou-se à ORDEM DO DIA, na qual foram apreciadas as seguintes matérias:

PROJETO DE [TIPO DE PROJETO] Nº [NÚMERO E ANO] – AUTOR: [AUTOR]. EMENTA: [EMENTA RESUMIDA DO PROJETO].

Situação: [RESULTADO DA VOTAÇÃO]

PROJETO DE [TIPO DE PROJETO] Nº [NÚMERO E ANO] – AUTOR: [AUTOR]. EMENTA: [EMENTA RESUMIDA DO PROJETO].

Situação: [RESULTADO DA VOTAÇÃO]

[LISTAR OUTROS PROJETOS AQUI]

Nas CONSIDERAÇÕES FINAIS, o Senhor Presidente [PRESIDENTE] se pronunciou sobre [RESUMO DOS PRINCIPAIS TEMAS ABORDADOS NAS CONSIDERAÇÕES FINAIS DO PRESIDENTE, CONFORME O RESUMO. Ex: Votação da LOA, trabalho das comissões, pronunciamento da Sra. Meire, apoio aos alunos da EEP Júlio França, Projeto Adote um Sorriso e agradecimento aos funcionários e secretarias parceiras].

Encerrou a presente sessão. Eu, [SECRETARIO], 1º Secretário, lavrei a presente ata, que, após lida e aprovada, será assinada pelo Presidente [PRESIDENTE] e por mim.

SALA DAS SESSÕES DA CÂMARA MUNICIPAL DE [MUNICÍPIO], ESTADO DO [ESTADO], aos [DATA POR EXTENSO].`;

      // Montar dados auxiliares da sessão para contextualizar a IA
      const nomesPresentes = presencasData
        .map((p: any) => p.nomeParlamentar || p.nome)
        .filter(Boolean)
        .join(", ");

      const projetosResumo = pautasData
        .map((p: any) => {
          const numero = p.numero || p.id;
          const tipo = p.pautaLegislativa || "";
          const autor = p.autor || "não informado";
          const ementa = (p.expressoes || "").slice(0, 240);
          return `- ${numero} ${
            tipo ? `(${tipo})` : ""
          } – Autor: ${autor}. Ementa: ${ementa}`;
        })
        .join("\n");

      const dadosSessaoTexto = `
Município/Legislatura: ${sessaoData?.legislatura || "não informado"}
Tipo de sessão: ${sessaoData?.tipoSessao || "não informado"}
Data da sessão: ${sessaoData?.dataSessao || "não informado"}
Parlamentares presentes: ${nomesPresentes || "não informado"}
Projetos/Pautas:\n${projetosResumo || "- não informado"}
`.trim();

      // Enviar para o ChatGPT com o modelo de ata
      const chatGptResponse = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              process.env.REACT_APP_OPENAI_API_KEY ||
              "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"
            }`,
          },
          body: JSON.stringify({
            model: "gpt-4",
            messages: [
              {
                role: "system",
                content: `Você é um assistente especializado em gerar atas de sessões legislativas. 
                Use o modelo fornecido como base e adapte as informações da transcrição para criar uma ata completa e formal.
                
                O modelo de ata é:
                ${modeloAtaCompacto}

                Transcrição da sessão atual (limitada):
${truncatedTranscription}

Instruções:
1. Use o modelo fornecido como base para criar a ata da sessão atual
2. Extraia da transcrição: data, horário, vereadores presentes, projetos discutidos, votações, etc.
3. Mantenha o formato formal e estrutura do modelo
4. Se informações não estiverem na transcrição, use "não especificado" ou similar
5. Gere uma ata completa seguindo o padrão legislativo

Os dados da sessão são:
${dadosSessaoTexto}
`,
              },
            ],
            temperature: 0.3,
            max_tokens: 1500,
          }),
        }
      );

      console.log("Resposta da API recebida:", chatGptResponse.status);

      if (!chatGptResponse.ok) {
        const errorText = await chatGptResponse.text();
        console.error("Erro na API:", chatGptResponse.status, errorText);
        throw new Error(
          `Erro na API: ${chatGptResponse.status} - ${errorText}`
        );
      }

      const data = await chatGptResponse.json();
      console.log("Dados da API recebidos:", data);

      const ataGerada = data.choices?.[0]?.message?.content || "";

      if (!ataGerada) {
        console.error("Ata gerada está vazia");
        throw new Error("Não foi possível gerar a ata - resposta vazia");
      }

      console.log(
        "Ata gerada com sucesso:",
        ataGerada.substring(0, 100) + "..."
      );

      // Salvar a ata gerada
      setCurrentStep("saving");
      console.log("Salvando ata no Firestore...");
      console.log("RoomId:", roomId);
      console.log("Transcription ID:", transcription.id);
      console.log(
        "Ata gerada (primeiros 200 chars):",
        ataGerada.substring(0, 200)
      );
      console.log("Tamanho da ata:", ataGerada.length, "caracteres");

      const db = getFirestore();

      try {
        // Verificar se a sessão existe primeiro
        const sessaoRef = doc(
          db,
          "users",
          `belacruz@eleg`,
          "sessoes",
          `${roomId}`
        );
        const sessaoDoc = await getDoc(sessaoRef);

        if (!sessaoDoc.exists()) {
          console.error("Sessão não encontrada:", roomId);
          throw new Error(`Sessão ${roomId} não encontrada no banco de dados`);
        }

        console.log("Sessão encontrada, salvando ata...");

        // Tentar primeiro com belacruz@eleg (mesmo padrão usado em outros lugares)
        const docRef = await addDoc(
          collection(
            db,
            "users",
            `belacruz@eleg`,
            "sessoes",
            `${roomId}`,
            "atasIA"
          ),
          {
            texto: ataGerada,
            criadoEm: new Date(),
            modeloUsado: "ChatGPT-4 (Modelo Legislativo)",
            transcricaoId: transcription.id,
            sessaoId: roomId,
            geradaComIA: true,
            status: "concluida",
            versao: "1.0",
          }
        );

        console.log(
          "Ata salva com sucesso no Firestore. Document ID:",
          docRef.id
        );

        // Verificar se o documento foi realmente salvo
        const docSnapshot = await getDoc(docRef);
        if (docSnapshot.exists()) {
          console.log("Documento confirmado no banco:", docSnapshot.data());
        } else {
          console.error("ERRO: Documento não foi encontrado após salvar!");
        }

        // Atualizar o estado local para mostrar a ata imediatamente
        setAtaGerada({
          id: docRef.id,
          texto: ataGerada,
          criadoEm: new Date(),
          modeloUsado: "Modelo padrão de ata legislativa",
          transcricaoId: transcription.id,
          sessaoId: roomId,
          geradaComIA: true,
          status: "concluida",
          versao: "1.0",
        });
      } catch (firestoreError) {
        console.error("Erro específico do Firestore:", firestoreError);

        // Tentar com belacruz@eleg como fallback
        try {
          console.log("Tentando com belacruz@eleg como fallback...");
          const docRef = await addDoc(
            collection(
              db,
              "users",
              `belacruz@eleg`,
              "sessoes",
              `${roomId}`,
              "atasIA"
            ),
            {
              texto: ataGerada,
              criadoEm: new Date(),
              modeloUsado: "ChatGPT-4 (Modelo Legislativo)",
              transcricaoId: transcription.id,
              sessaoId: roomId,
              geradaComIA: true,
              status: "concluida",
              versao: "1.0",
            }
          );

          console.log(
            "Ata salva com sucesso no Firestore (fallback). Document ID:",
            docRef.id
          );

          // Verificar se o documento foi realmente salvo
          const docSnapshot = await getDoc(docRef);
          if (docSnapshot.exists()) {
            console.log(
              "Documento confirmado no banco (fallback):",
              docSnapshot.data()
            );
          } else {
            console.error(
              "ERRO: Documento não foi encontrado após salvar (fallback)!"
            );
          }

          setAtaGerada({
            id: docRef.id,
            texto: ataGerada,
            criadoEm: new Date(),
            modeloUsado: "ChatGPT-4 (Modelo Legislativo)",
            transcricaoId: transcription.id,
            sessaoId: roomId,
            geradaComIA: true,
            status: "concluida",
            versao: "1.0",
          });
        } catch (fallbackError) {
          console.error("Erro no fallback também:", fallbackError);
          throw new Error(
            `Erro ao salvar no Firestore: ${firestoreError}. Fallback também falhou: ${fallbackError}`
          );
        }
      }
    } catch (e) {
      console.error("Erro completo ao gerar ata:", e);
      setError(
        e instanceof Error
          ? e.message
          : "Erro ao gerar ata. Verifique sua conexão e tente novamente."
      );
    } finally {
      setLoading(false);
      setCurrentStep("idle");
    }
  };

  const getStepText = () => {
    switch (currentStep) {
      case "generating":
        return "Gerando ata com Inteligência Artificial...";
      case "saving":
        return "Salvando ata no sistema...";
      default:
        return "";
    }
  };

  // Função para testar salvamento direto (debug)
  const testSaveAta = async () => {
    console.log("=== TESTE DE SALVAMENTO DIRETO ===");

    if (!roomId) {
      console.error("RoomId não encontrado");
      return;
    }

    const db = getFirestore();
    const testData = {
      texto: "TESTE DE ATA GERADA COM IA - " + new Date().toISOString(),
      criadoEm: new Date(),
      modeloUsado: "ChatGPT-4 (Teste)",
      transcricaoId: "test-" + Date.now(),
      sessaoId: roomId,
      geradaComIA: true,
      status: "concluida",
      versao: "1.0",
    };

    try {
      console.log("Tentando salvar dados de teste:", testData);

      const docRef = await addDoc(
        collection(
          db,
          "users",
          `belacruz@eleg`,
          "sessoes",
          `${roomId}`,
          "atasIA"
        ),
        testData
      );

      console.log("✅ TESTE: Documento salvo com ID:", docRef.id);

      // Verificar se foi realmente salvo
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        console.log(
          "✅ TESTE: Documento confirmado no banco:",
          docSnapshot.data()
        );
      } else {
        console.error("❌ TESTE: Documento não encontrado após salvar!");
      }
    } catch (error) {
      console.error("❌ TESTE: Erro ao salvar:", error);
    }
  };

  if (loadingAta) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        flexDirection="column"
        gap={2}
      >
        <LinearProgress sx={{ width: 200 }} />
        <Typography variant="h6" color="text.secondary">
          Carregando informações da ata...
        </Typography>
      </Box>
    );
  }

  if (!transcription) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        flexDirection="column"
        gap={2}
      >
        <LinearProgress sx={{ width: 200 }} />
        <Typography variant="h6" color="text.secondary">
          Carregando transcrição...
        </Typography>
      </Box>
    );
  }

  // Se a ata já foi gerada, mostrar visualização diferente
  if (ataGerada) {
    return (
      <>
        {/* Modal para mostrar ata completa */}
        <Dialog
          open={showAtaCompleta}
          onClose={() => setShowAtaCompleta(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: "90vh",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: "1px solid #e5e7eb" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    Ata Completa da Sessão
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gerada em:{" "}
                    {ataGerada?.criadoEm
                      ? format(
                          ataGerada.criadoEm.toDate?.() ||
                            new Date(ataGerada.criadoEm),
                          "d 'de' MMMM 'de' yyyy 'às' HH:mm",
                          { locale: ptBR }
                        )
                      : "Data não disponível"}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setShowAtaCompleta(false)}
                  sx={{ color: "text.secondary" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ p: 3, maxHeight: "70vh", overflowY: "auto" }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  backgroundColor: "#fafafa",
                  border: "1px solid #e5e7eb",
                  "&::-webkit-scrollbar": {
                    width: 8,
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "grey.200",
                    borderRadius: 4,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "grey.400",
                    borderRadius: 4,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-line",
                    lineHeight: 1.8,
                    color: "text.primary",
                    fontFamily: "serif",
                    fontSize: "1.1rem",
                  }}
                >
                  {ataGerada?.texto || "Ata não encontrada"}
                </Typography>
              </Paper>
            </Box>

            <Box
              sx={{ p: 3, borderTop: "1px solid #e5e7eb", bgcolor: "#f9fafb" }}
            >
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowAtaCompleta(false)}
                >
                  Fechar
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    // Função para imprimir ou salvar a ata
                    const printWindow = window.open("", "_blank");
                    if (printWindow) {
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>Ata da Sessão</title>
                            <style>
                              body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
                              .header { text-align: center; margin-bottom: 30px; }
                              .content { white-space: pre-line; }
                            </style>
                          </head>
                          <body>
                            <div class="header">
                              <h1>ATA DA SESSÃO</h1>
                              <p>Gerada em: ${
                                ataGerada?.criadoEm
                                  ? format(
                                      ataGerada.criadoEm.toDate?.() ||
                                        new Date(ataGerada.criadoEm),
                                      "d 'de' MMMM 'de' yyyy 'às' HH:mm",
                                      { locale: ptBR }
                                    )
                                  : "Data não disponível"
                              }</p>
                            </div>
                            <div class="content">${ataGerada?.texto || ""}</div>
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.print();
                    }
                  }}
                  startIcon={<DescriptionIcon />}
                >
                  Imprimir Ata
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Stack spacing={4}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{ color: "text.secondary" }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Ata Já Gerada
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Esta sessão já possui uma ata gerada automaticamente
                </Typography>
              </Box>
            </Box>

            {/* Card de sucesso */}
            <Card
              sx={{
                background: "linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)",
                border: "1px solid #c084fc",
                borderRadius: "16px",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: "50%",
                      bgcolor: "success.main",
                      color: "white",
                    }}
                  >
                    <AutoAwesomeIcon />
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="success.dark"
                    >
                      Ata Gerada com Sucesso
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      A ata foi criada automaticamente e está pronta para uso
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={3}>
                  {/* Informações da ata */}
                  <Box
                    sx={{
                      bgcolor: "background.paper",
                      p: 3,
                      borderRadius: 2,
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Informações da Ata
                    </Typography>
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Gerada em:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {ataGerada.criadoEm
                            ? format(
                                ataGerada.criadoEm.toDate?.() ||
                                  new Date(ataGerada.criadoEm),
                                "d 'de' MMMM 'de' yyyy 'às' HH:mm",
                                { locale: ptBR }
                              )
                            : "Data não disponível"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Modelo usado:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {ataGerada.modeloUsado || "Modelo padrão"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          ID da transcrição:
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {ataGerada.transcricaoId || "N/A"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  {/* Preview da ata */}
                  <Box
                    sx={{
                      bgcolor: "background.paper",
                      p: 3,
                      borderRadius: 2,
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Prévia da Ata
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        maxHeight: 300,
                        overflowY: "auto",
                        backgroundColor: "grey.50",
                        "&::-webkit-scrollbar": {
                          width: 8,
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "grey.200",
                          borderRadius: 4,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "grey.400",
                          borderRadius: 4,
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-line",
                          lineHeight: 1.6,
                          color: "text.secondary",
                        }}
                      >
                        {ataGerada.texto?.substring(0, 500) + "..."}
                      </Typography>
                    </Paper>
                  </Box>

                  {/* Ações */}
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={() => setShowAtaCompleta(true)}
                      startIcon={<DescriptionIcon />}
                      sx={{ minWidth: 200 }}
                    >
                      Ver Ata Completa
                    </Button>

                    <Button
                      variant="outlined"
                      color="secondary"
                      size="large"
                      onClick={() =>
                        navigate(`/sessoes/${roomId}/resumo/midia`)
                      }
                      sx={{ minWidth: 200 }}
                    >
                      Editar Transcrição
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      size="large"
                      onClick={() => {
                        // Aqui você pode implementar a regeneração da ata
                        console.log("Regenerar ata");
                      }}
                      sx={{ minWidth: 200 }}
                    >
                      Regenerar Ata
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Container>
      </>
    );
  }

  return (
    <>
      {/* Modal para mostrar ata completa */}
      <Dialog
        open={showAtaCompleta}
        onClose={() => setShowAtaCompleta(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: "1px solid #e5e7eb" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Ata Completa da Sessão
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Gerada em:{" "}
                  {ataGerada?.criadoEm
                    ? format(
                        ataGerada.criadoEm.toDate?.() ||
                          new Date(ataGerada.criadoEm),
                        "d 'de' MMMM 'de' yyyy 'às' HH:mm",
                        { locale: ptBR }
                      )
                    : "Data não disponível"}
                </Typography>
              </Box>
              <IconButton
                onClick={() => setShowAtaCompleta(false)}
                sx={{ color: "text.secondary" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ p: 3, maxHeight: "70vh", overflowY: "auto" }}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                backgroundColor: "#fafafa",
                border: "1px solid #e5e7eb",
                "&::-webkit-scrollbar": {
                  width: 8,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "grey.200",
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "grey.400",
                  borderRadius: 4,
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: "pre-line",
                  lineHeight: 1.8,
                  color: "text.primary",
                  fontFamily: "serif",
                  fontSize: "1.1rem",
                }}
              >
                {ataGerada?.texto || "Ata não encontrada"}
              </Typography>
            </Paper>
          </Box>

          <Box
            sx={{ p: 3, borderTop: "1px solid #e5e7eb", bgcolor: "#f9fafb" }}
          >
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => setShowAtaCompleta(false)}
              >
                Fechar
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  // Função para imprimir ou salvar a ata
                  const printWindow = window.open("", "_blank");
                  if (printWindow) {
                    printWindow.document.write(`
                      <html>
                        <head>
                          <title>Ata da Sessão</title>
                          <style>
                            body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
                            .header { text-align: center; margin-bottom: 30px; }
                            .content { white-space: pre-line; }
                          </style>
                        </head>
                        <body>
                          <div class="header">
                            <h1>ATA DA SESSÃO</h1>
                            <p>Gerada em: ${
                              ataGerada?.criadoEm
                                ? format(
                                    ataGerada.criadoEm.toDate?.() ||
                                      new Date(ataGerada.criadoEm),
                                    "d 'de' MMMM 'de' yyyy 'às' HH:mm",
                                    { locale: ptBR }
                                  )
                                : "Data não disponível"
                            }</p>
                          </div>
                          <div class="content">${ataGerada?.texto || ""}</div>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                    printWindow.print();
                  }
                }}
                startIcon={<DescriptionIcon />}
              >
                Imprimir Ata
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack spacing={4}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ color: "text.secondary" }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Gerar Ata Automática
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Revise a transcrição e gere a ata final usando IA
              </Typography>
            </Box>
          </Box>

          {/* Progress Indicator */}
          {loading && (
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LinearProgress
                      variant="indeterminate"
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                  <Typography variant="body2" color="primary">
                    {getStepText()}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Transcription Preview */}
          <Card>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <DescriptionIcon color="primary" />
                <Typography variant="h6" fontWeight="medium">
                  Transcrição da Sessão
                </Typography>
                <Chip
                  label={`${
                    transcription.text?.split(" ").length || 0
                  } palavras`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  maxHeight: 400,
                  overflowY: "auto",
                  backgroundColor: "grey.50",
                  "&::-webkit-scrollbar": {
                    width: 8,
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "grey.200",
                    borderRadius: 4,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "grey.400",
                    borderRadius: 4,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-line",
                    lineHeight: 1.6,
                    color: "text.secondary",
                  }}
                >
                  {transcription.text}
                </Typography>
              </Paper>
            </CardContent>
          </Card>

          {/* Modelo de Ata Info */}
          <Card>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <AutoAwesomeIcon color="primary" />
                <Typography variant="h6" fontWeight="medium">
                  Modelo de Ata Legislativa
                </Typography>
                <Chip
                  label="Padrão Oficial"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A ata será gerada automaticamente seguindo o formato oficial da
                Câmara Municipal, adaptando as informações específicas desta
                sessão baseadas na transcrição.
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  maxHeight: 200,
                  overflowY: "auto",
                  backgroundColor: "primary.50",
                  borderColor: "primary.200",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-line",
                    lineHeight: 1.4,
                    color: "text.secondary",
                    fontSize: "0.875rem",
                  }}
                >
                  ATA DA SESSÃO LEGISLATIVA Aos [DATA] às [HORA] no paço da
                  Câmara Municipal, REUNIRAM-SE SOB A PRESIDÊNCIA DE
                  [PRESIDENTE]; VICE-PRESIDENTE: [VICE-PRESIDENTE]; PRIMEIRO
                  SECRETARIO: [SECRETARIO1]; Segunda SECRETARIA: [SECRETARIO2],
                  VEREADORES: [LISTA_VEREADORES]. EM SESSÃO [TIPO] LEGISLATIVA
                  DO EXERCÍCIO DE [ANO]. A senhora presidente solicitou a
                  primeira Secretária que fizesse a chamada dos vereadores para
                  verificação de quórum, após a verificação do número legal de
                  vereadores, a presidente declarou aberta a sessão sobre a
                  proteção Divina e em nome do povo e pediu a secretária que
                  fizesse a leitura do material do expediente que constou:
                  [MATERIAIS]. Seguidamente a Sra. Presidente passou para o
                  segundo expediente e facultou a palavra, [INTERVENCOES]. Em
                  seguida passando para o terceiro expediente a presidente
                  colocou em [PROJETOS], [RESULTADOS]. Seguidamente como nada
                  havia a tratar na presente sessão a Sra. presidente declarou
                  encerrada a sessão, e eu [SECRETARIO], lavrei a presente ata
                  que segue assinada por mim e pelo presidente.
                </Typography>
              </Paper>
            </CardContent>
          </Card>

          {/* Action Button */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleGenerateAta}
              disabled={loading}
              startIcon={<AutoAwesomeIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: "medium",
                minWidth: 250,
              }}
            >
              {loading ? "Gerando Ata..." : "Gerar Ata com IA"}
            </Button>
          </Box>
        </Stack>
      </Container>
    </>
  );
}
